import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CardLoading from "./CardLoading";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Grid, GridColumn as Column, GridCellProps, GridPageChangeEvent, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { formatPhoneNumber } from "src/utils/formatUtils";
import { toast } from "sonner";
import { TC } from "../utils/trustCallApi";
import { webhookSettingsSchema } from "../utils/validationSchemas";
import { useCanWrite, AGENT_READ_ONLY_MESSAGE } from "../hooks/useAccountConfig";
import AgentReadOnlyBanner from "./AgentReadOnlyBanner";

type WebhookFormData = z.infer<typeof webhookSettingsSchema>;

// Matches MVC payload preview exactly (Monitor.cshtml line 1545-1562)
const PAYLOAD_PREVIEW = `POST https://your-url.com/webhook

Headers:
  Content-Type: application/json
  X-TrustCall-Signature: sha256=a1b2c3d4e5f6...
  X-TrustCall-Event: tn-flagged

Body:
{
  "event": "tn-flagged",
  "phoneNumber": "5551234567",
  "carrier": "Verizon",
  "previousStatus": "Clean",
  "newStatus": "Flagged",
  "acctId": "DEMO",
  "screenshotUrl": "https://example.com/screenshots/sample.png",
  "timestamp": "2026-04-09T10:30:00Z"
}`;

// Field names match C# TrustCallWebhookLog entity (camelCase)
interface DeliveryLog {
  trustCallWebhookLogId: number;
  phoneNumber: string;
  carrier: string;
  eventType: string;
  httpStatusCode: number;
  createdDate: string;
  requestPayload?: string;
  responseBody?: string;
  isSuccess: boolean;
}

const WebhookSettings: React.FC = () => {
  const queryClient = useQueryClient();

  // Matches MVC _webhookFullSecret — null when masked, set after rotate
  const rawSecretRef = React.useRef<string | null>(null);
  const maskTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [signingSecret, setSigningSecret] = useState("");
  // Matches MVC _savedWebhookUrl — tracks last-saved URL for Test button gating
  const [savedWebhookUrl, setSavedWebhookUrl] = useState("");

  useEffect(() => {
    return () => {
      if (maskTimerRef.current) clearTimeout(maskTimerRef.current);
    };
  }, []);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ["trustcall", "settings", "webhooks"],
    queryFn: () => callApi(TC.SETTINGS_WEBHOOKS, HttpMethod.GET),
  });

  const deliveriesQuery = useQuery<DeliveryLog[]>({
    queryKey: ["trustcall", "settings", "webhooks", "deliveries"],
    queryFn: () => callApi(TC.SETTINGS_WEBHOOKS_DELIVERIES, HttpMethod.GET),
  });

  // DNC-390: the Agent role may read the webhook configuration and the delivery
  // log but may not save, test, rotate the secret, retry a delivery, or flip the
  // enable switch. Copy Secret and Copy Payload stay available — both are reads.
  // Declared here, above the isLoading early return, so the hook call is
  // unconditional.
  const canWrite = useCanWrite();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm<WebhookFormData>({
    resolver: zodResolver(webhookSettingsSchema),
    defaultValues: {
      webhookUrl: "",
      isActive: false,
      notifyVerizon: true,
      notifyATT: true,
      notifyTMobile: true,
      notifyOnFlagRemoved: false,
    },
  });

  const isActive = watch("isActive");
  const currentUrl = watch("webhookUrl");

  // Matches MVC loadWebhookSettings — populates form, tracks saved URL, masks secret
  useEffect(() => {
    if (settingsQuery.data) {
      const webhook = settingsQuery.data.webhook ?? settingsQuery.data;
      reset({
        webhookUrl: webhook.webhookUrl ?? "",
        isActive: webhook.isActive ?? false,
        notifyVerizon: webhook.notifyVerizon ?? true,
        notifyATT: webhook.notifyATT ?? true,
        notifyTMobile: webhook.notifyTMobile ?? true,
        notifyOnFlagRemoved: webhook.notifyOnFlagRemoved ?? false,
      });
      // Track saved URL for Test button gating (matches MVC _savedWebhookUrl)
      setSavedWebhookUrl((webhook.webhookUrl || "").trim());
      // Mask signing secret (matches MVC maskedSecret display)
      rawSecretRef.current = null;
      const maskedSecret = settingsQuery.data.maskedSecret ?? "";
      setSigningSecret(maskedSecret);
    } else {
      // No webhook configured yet (matches MVC else branch)
      setSavedWebhookUrl("");
      rawSecretRef.current = null;
      setSigningSecret("");
    }
  }, [settingsQuery.data, reset]);

  // No default onSuccess/onError — each call site provides its own to avoid
  // TanStack Query firing both definition-level AND mutate-level callbacks.
  const saveMutation = useMutation({
    mutationFn: (data: WebhookFormData) =>
      callApi(TC.SETTINGS_WEBHOOKS, HttpMethod.PUT, data),
  });

  const testMutation = useMutation({
    mutationFn: () => callApi(TC.SETTINGS_WEBHOOKS_TEST, HttpMethod.POST),
    onSuccess: (data: { isSuccess?: boolean; httpStatusCode?: number; errorMessage?: string }) => {
      // Matches MVC test success/error handling
      if (data.isSuccess) {
        toast.success(`Test webhook delivered successfully (HTTP ${data.httpStatusCode}).`);
      } else {
        toast.error(`Test webhook failed${data.httpStatusCode ? ` (HTTP ${data.httpStatusCode})` : ""}. ${data.errorMessage || ""}`);
      }
      // Matches MVC: loadWebhookDeliveries() after test
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "webhooks", "deliveries"] });
    },
    onError: () => toast.error("Failed to send test webhook."),
  });

  const rotateMutation = useMutation({
    mutationFn: () => callApi(TC.SETTINGS_WEBHOOKS_ROTATE, HttpMethod.POST),
    onSuccess: (data: { isSuccess?: boolean; secret?: string; errorMessage?: string }) => {
      // Matches MVC rotate success handler
      if (data.isSuccess && data.secret) {
        rawSecretRef.current = data.secret;
        setSigningSecret(data.secret);
        toast.success("Signing secret rotated. Copy it now \u2014 it won't be shown again.");
        // Auto-mask after 30 seconds (matches MVC setTimeout)
        if (maskTimerRef.current) clearTimeout(maskTimerRef.current);
        maskTimerRef.current = setTimeout(() => {
          if (rawSecretRef.current === data.secret) {
            // Matches MVC: '****' + last 4 chars
            const masked = "****" + data.secret!.substring(data.secret!.length - 4);
            setSigningSecret(masked);
            rawSecretRef.current = null;
          }
        }, 30000);
      } else {
        toast.error(data.errorMessage || "Failed to rotate secret.");
      }
    },
    onError: () => toast.error("Failed to rotate signing secret."),
  });

  const retryMutation = useMutation({
    mutationFn: (logId: number) =>
      callApi(`${TC.SETTINGS_WEBHOOKS_RETRY}/${logId}/retry`, HttpMethod.POST),
    onSuccess: (result: { isSuccess?: boolean; httpStatusCode?: number }) => {
      // Matches MVC retry success/error handling
      if (result.isSuccess) {
        toast.success(`Webhook retry delivered successfully (HTTP ${result.httpStatusCode}).`);
      } else {
        toast.error(`Webhook retry failed${result.httpStatusCode ? ` (HTTP ${result.httpStatusCode})` : ""}.`);
      }
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "webhooks", "deliveries"] });
    },
    onError: () => toast.error("Failed to retry webhook delivery."),
  });

  // Delivery grid state — matches MVC: pageSize 10, default sort createdDate desc
  const [deliveryPage, setDeliveryPage] = useState({ skip: 0, take: 10 });
  const [deliverySort, setDeliverySort] = useState<SortDescriptor[]>([{ field: "createdDate", dir: "desc" }]);
  const [expandedDeliveryIds, setExpandedDeliveryIds] = useState<Set<number>>(new Set());

  // Matches MVC #saveWebhookSettings validation exactly
  const onSubmit = (data: WebhookFormData) => {
    const url = data.webhookUrl.trim();
    if (url !== "" && !url.toLowerCase().startsWith("https://")) {
      toast.warning("Webhook URL must use HTTPS.");
      return;
    }
    if (data.isActive) {
      if (url === "") {
        toast.warning("An active webhook requires a non-empty HTTPS URL.");
        return;
      }
      if (!data.notifyVerizon && !data.notifyATT && !data.notifyTMobile) {
        toast.warning("Select at least one carrier (Verizon, AT&T, or T-Mobile) to notify.");
        return;
      }
    }
    saveMutation.mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "webhooks"] });
        toast.success("Webhook settings saved successfully.");
      },
      onError: () => toast.error("Failed to save webhook settings."),
    });
  };

  // Matches MVC #testWebhookBtn click — must save before testing
  const handleTestWebhook = () => {
    const url = (currentUrl || "").trim();
    if (savedWebhookUrl === "" || url !== savedWebhookUrl) {
      toast.warning("Save your webhook settings before testing.");
      return;
    }
    testMutation.mutate();
  };

  // Matches MVC #copySecretBtn click — disabled when masked
  const handleCopySecret = () => {
    if (!rawSecretRef.current) {
      toast.warning("The signing secret is masked. Rotate it to reveal and copy a new one.");
      return;
    }
    navigator.clipboard.writeText(rawSecretRef.current).then(() => {
      toast.success("Signing secret copied to clipboard.");
    }).catch(() => {
      toast.error("Failed to copy to clipboard.");
    });
  };

  // Matches MVC #copyPayloadBtn click
  const handleCopyPayload = () => {
    navigator.clipboard.writeText(PAYLOAD_PREVIEW).then(() => {
      toast.success("Payload preview copied to clipboard.");
    }).catch(() => {
      toast.error("Failed to copy to clipboard.");
    });
  };

  if (settingsQuery.isLoading) {
    return <CardLoading />;
  }

  // Matches MVC: Test button enabled only when savedUrl is non-empty and matches current
  const isTestEnabled = savedWebhookUrl !== "" && (currentUrl || "").trim() === savedWebhookUrl;
  // Matches MVC: Copy button enabled only when full secret is available (after rotate)
  const isCopyEnabled = !!rawSecretRef.current;
  const agentTitle = !canWrite ? AGENT_READ_ONLY_MESSAGE : undefined;

  const deliveries = deliveriesQuery.data ?? [];

  return (
    <div>
      <AgentReadOnlyBanner />
      {/* Header with toggle — matches MVC layout */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Webhook Notifications</h5>
        <div className="d-flex align-items-center gap-2">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="webhookEnabled"
              style={{
                width: "3em",
                height: "1.5em",
                cursor: "pointer",
              }}
              checked={isActive}
              disabled={!canWrite}
              onChange={() => {
                if (isActive) {
                  // Matches MVC: revert toggle until confirmed via modal
                  setShowDisableModal(true);
                } else {
                  // Matches MVC: toggle on immediately, user must Save to persist
                  setValue("isActive", true);
                }
              }}
            />
          </div>
          <span className="fw-bold" style={{ color: "var(--bs-body-color)" }}>
            {isActive ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      {/* Disabled state — matches MVC #webhookDisabledContainer */}
      {!isActive && (
        <div
          className="text-center py-5 px-3 mb-3"
          style={{
            backgroundColor: "var(--card-bg)",
            border: "2px dashed var(--inputbox-border-color)",
            borderRadius: 6,
            color: "var(--bs-body-color)",
          }}
        >
          <div style={{ fontSize: 48, opacity: 0.5, marginBottom: 12 }}>
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
              <path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2m6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1z" />
            </svg>
          </div>
          <h5 className="fw-bold mb-2">Webhook Notifications are Disabled</h5>
          <p className="mb-3" style={{ maxWidth: 480, margin: "0 auto", opacity: 0.7 }}>
            Get real-time HTTP callbacks when carrier spam scores change for your
            phone numbers. Enable the toggle above to configure your webhook
            endpoint and carrier triggers.
          </p>
          <div className="d-flex justify-content-center gap-4">
            <span><span style={{ color: "#198754" }}>&#10003;</span> Real-time alerts</span>
            <span><span style={{ color: "#198754" }}>&#10003;</span> HMAC-SHA256 signed</span>
            <span><span style={{ color: "#198754" }}>&#10003;</span> Per-carrier filtering</span>
          </div>
        </div>
      )}

      {/* Enabled state — matches MVC #webhookSettingsContainer */}
      {isActive && <form onSubmit={handleSubmit(onSubmit)}>
        {/* Top row: Configuration + Carrier Triggers */}
        <div className="row mb-3 align-items-stretch">
          {/* Left: Webhook Configuration */}
          <div className="col-md-6 d-flex">
            <div className="card w-100">
              <div className="card-header tc-card-header">
                <h6 className="mb-0" style={{ color: "white" }}>Webhook Configuration</h6>
              </div>
              <div className="card-body">
                {/* Webhook URL — matches MVC: type="text", placeholder, border style */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Webhook URL</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://your-url.com/webhook"
                      style={{ border: "1px solid rgb(169, 169, 169)" }}
                      {...register("webhookUrl")}
                      // DNC-390: readOnly, not disabled — the URL must stay copyable.
                      readOnly={!canWrite}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleTestWebhook}
                      disabled={!isTestEnabled || testMutation.isPending || !canWrite}
                      title={agentTitle ?? (isTestEnabled ? "Send a test event to the saved webhook URL." : "Save webhook settings before testing.")}
                    >
                      {testMutation.isPending ? "Testing..." : "▶ Test"}
                    </button>
                  </div>
                  <small className="text-muted">
                    Must use HTTPS. We'll POST carrier score changes to this URL.
                  </small>
                </div>

                {/* Signing Secret — matches MVC: monospace, bg, placeholder, disabled states */}
                <div>
                  <label className="form-label fw-bold">Signing Secret</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      value={signingSecret}
                      placeholder="Save webhook to generate a secret"
                      style={{ backgroundColor: "var(--right-sidenav-bg)", color: "var(--bs-body-color)", fontFamily: "monospace", border: "1px solid var(--inputbox-border-color)" }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCopySecret}
                      disabled={!isCopyEnabled}
                      title={isCopyEnabled ? "Copy signing secret to clipboard." : "Rotate the signing secret to reveal and copy it."}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}><path d="M20.91 2.483H7.95a.607.607 0 0 0-.607.607v4.253H3.09a.607.607 0 0 0-.607.607v12.96a.607.607 0 0 0 .607.607h12.96a.607.607 0 0 0 .607-.607v-4.253h4.253a.607.607 0 0 0 .607-.607V3.09a.607.607 0 0 0-.607-.607m-5.467 17.82H3.697V8.558h11.746zm4.86-4.86h-3.645V7.95a.607.607 0 0 0-.607-.607H8.558V3.697h11.745z"></path></svg>
                      Copy
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={() => setShowRotateModal(true)}
                      disabled={rotateMutation.isPending || !canWrite}
                      title={agentTitle}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}><path d="M23.137 4.71v4.86a.607.607 0 0 1-.607.607h-4.86a.608.608 0 0 1 0-1.215h3.295L17.89 6.146l-.02-.019a8.303 8.303 0 1 0-.171 11.913.608.608 0 0 1 .834.884A9.48 9.48 0 0 1 12 21.517h-.13a9.517 9.517 0 1 1 6.85-16.261l3.203 2.932V4.71a.607.607 0 1 1 1.214 0"></path></svg>
                      {rotateMutation.isPending ? "Rotating..." : "Rotate"}
                    </button>
                  </div>
                  <small className="text-muted">
                    Used to verify webhook payloads via HMAC-SHA256 (<code>X-TrustCall-Signature</code> header).
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Carrier Triggers — matches MVC layout and labels */}
          <div className="col-md-6 d-flex">
            <div className="card w-100">
              <div className="card-header tc-card-header">
                <h6 className="mb-0" style={{ color: "white" }}>Carrier Triggers</h6>
              </div>
              <div className="card-body" style={{ padding: 23 }}>
                <p style={{ marginBottom: 15 }}>Select which carrier score changes should trigger a webhook notification:</p>
                <div className="form-check" style={{ marginBottom: 10 }}>
                  <input className="form-check-input" type="checkbox" id="notifyVerizon" {...register("notifyVerizon")} disabled={!canWrite} />
                  <label className="form-check-label" htmlFor="notifyVerizon" style={{ fontWeight: "normal" }}>Verizon</label>
                </div>
                <div className="form-check" style={{ marginBottom: 10 }}>
                  <input className="form-check-input" type="checkbox" id="notifyATT" {...register("notifyATT")} disabled={!canWrite} />
                  <label className="form-check-label" htmlFor="notifyATT" style={{ fontWeight: "normal" }}>AT&T</label>
                </div>
                <div className="form-check" style={{ marginBottom: 10 }}>
                  <input className="form-check-input" type="checkbox" id="notifyTMobile" {...register("notifyTMobile")} disabled={!canWrite} />
                  <label className="form-check-label" htmlFor="notifyTMobile" style={{ fontWeight: "normal" }}>T-Mobile</label>
                </div>
                <hr style={{ marginTop: 10 }} />
                <div className="form-check" style={{ marginTop: 10 }}>
                  <input className="form-check-input" type="checkbox" id="notifyOnFlagRemoved" {...register("notifyOnFlagRemoved")} disabled={!canWrite} />
                  <label className="form-check-label" htmlFor="notifyOnFlagRemoved" style={{ fontWeight: "normal" }}>
                    Also notify when a flag is removed (<code>tn-clean</code>)
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payload Preview — matches MVC dark-bg code block */}
        <div className="card mb-3">
          <div className="card-header tc-card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ color: "white" }}>Payload Preview</h6>
            <button type="button" className="btn btn-outline-light btn-sm" onClick={handleCopyPayload}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}><path d="M20.91 2.483H7.95a.607.607 0 0 0-.607.607v4.253H3.09a.607.607 0 0 0-.607.607v12.96a.607.607 0 0 0 .607.607h12.96a.607.607 0 0 0 .607-.607v-4.253h4.253a.607.607 0 0 0 .607-.607V3.09a.607.607 0 0 0-.607-.607m-5.467 17.82H3.697V8.558h11.746zm4.86-4.86h-3.645V7.95a.607.607 0 0 0-.607-.607H8.558V3.697h11.745z"></path></svg>
              Copy
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <pre style={{ margin: 0, padding: 15, backgroundColor: "#1e1e1e", color: "#d4d4d4", borderRadius: "0 0 4px 4px", fontSize: 13, overflowX: "auto", whiteSpace: "pre" }}>
              {PAYLOAD_PREVIEW}
            </pre>
          </div>
        </div>

        {/* Save button — matches MVC btn-primary btn-lg btn-block */}
        <button
          type="submit"
          className="btn btn-primary btn-lg w-100 mb-4"
          disabled={saveMutation.isPending || !canWrite}
          title={agentTitle}
        >
          <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
            <path d="M384 32H64c-17.6 0-32 14.4-32 32v384c0 17.6 14.4 32 32 32h384c17.6 0 32-14.4 32-32V128zm-64 32v128h-64V64zm128 384-383.9.1-.1-.1V64.1l.1-.1H96v160h256V64h18.7l77.3 77.3z" />
          </svg>
          <span style={{ verticalAlign: "middle" }}>{saveMutation.isPending ? "Saving..." : "Save Webhook"}</span>
        </button>
      </form>}

      {/* Recent Deliveries — matches MVC kendoGrid config */}
      {isActive && (
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header tc-card-header">
          <h6 className="mb-0" style={{ color: "white" }}>Recent Deliveries</h6>
        </div>
        <div className="tc-hide-hierarchy" style={{ padding: 0 }}>
          {(() => {
            const sorted = orderBy(deliveries, deliverySort);
            const paged = sorted.slice(deliveryPage.skip, deliveryPage.skip + deliveryPage.take);
            const gridData = paged.map((d) => ({
              ...d,
              expanded: expandedDeliveryIds.has(d.trustCallWebhookLogId),
            }));
            return (
              <Grid
                data={gridData}
                total={deliveries.length}
                dataItemKey="trustCallWebhookLogId"
                style={{ minHeight: 400 }}
                scrollable="scrollable"
                sortable={{ mode: "single", allowUnsort: true }}
                sort={deliverySort}
                onSortChange={(e: GridSortChangeEvent) => setDeliverySort(e.sort)}
                pageable={{ buttonCount: 5, pageSizes: [10, 25, 50, 100] }}
                skip={deliveryPage.skip}
                take={deliveryPage.take}
                onPageChange={(e: GridPageChangeEvent) => setDeliveryPage({ skip: e.page.skip, take: e.page.take })}
                expandField="expanded"
                onExpandChange={(e) => {
                  const id = e.dataItem.trustCallWebhookLogId as number;
                  setExpandedDeliveryIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  });
                }}
                detail={({ dataItem }: { dataItem: DeliveryLog }) => (
                  <div className="row g-3 p-2">
                    <div className="col-md-6">
                      <strong>Request Payload</strong>
                      <pre className="tc-json-block" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 300, overflow: "auto", fontSize: 11, margin: 0, padding: 8 }}>
                        {(() => { try { return JSON.stringify(JSON.parse(dataItem.requestPayload || "{}"), null, 2); } catch { return dataItem.requestPayload || ""; } })()}
                      </pre>
                    </div>
                    <div className="col-md-6">
                      <strong>Response Body</strong>
                      <pre className="tc-json-block" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 300, overflow: "auto", fontSize: 11, margin: 0, padding: 8 }}>
                        {(() => { try { return JSON.stringify(JSON.parse(dataItem.responseBody || "{}"), null, 2); } catch { return dataItem.responseBody || ""; } })()}
                      </pre>
                    </div>
                  </div>
                )}
              >
                <Column
                  field="isSuccess"
                  title="Status"
                  width={100}
                  cell={(props: GridCellProps) => {
                    const ok = props.dataItem.isSuccess;
                    const code = props.dataItem.httpStatusCode;
                    return (
                      <td>
                        <span className={`badge ${ok ? "bg-success" : "bg-danger"}`}>
                          {ok ? "\u2713" : "\u2717"} {code || "ERR"}
                        </span>
                      </td>
                    );
                  }}
                />
                <Column
                  field="phoneNumber"
                  title="Phone"
                  cell={(props: GridCellProps) => (
                    <td><span style={{ fontWeight: "bold" }}>{formatPhoneNumber(props.dataItem.phoneNumber || "")}</span></td>
                  )}
                />
                <Column field="carrier" title="Carrier" />
                <Column field="eventType" title="Event" />
                <Column field="createdDate" title="Time" />
                <Column
                  title=" "
                  width={70}
                  sortable={false}
                  cell={(props: GridCellProps) => (
                    <td>
                      <a
                        href="#"
                        className="text-primary"
                        style={{ fontSize: 11 }}
                        onClick={(e) => {
                          e.preventDefault();
                          const id = props.dataItem.trustCallWebhookLogId as number;
                          setExpandedDeliveryIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(id)) next.delete(id); else next.add(id);
                            return next;
                          });
                        }}
                      >
                        JSON
                      </a>
                    </td>
                  )}
                />
                <Column
                  title=" "
                  width={70}
                  sortable={false}
                  cell={(props: GridCellProps) => {
                    if (!props.dataItem.isSuccess) {
                      return (
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary retryWebhookBtn"
                            title={agentTitle ?? "Retry"}
                            disabled={retryMutation.isPending || !canWrite}
                            onClick={() => retryMutation.mutate(props.dataItem.trustCallWebhookLogId)}
                          >
                            ↻
                          </button>
                        </td>
                      );
                    }
                    return <td />;
                  }}
                />
              </Grid>
            );
          })()}
        </div>
      </div>
      )}

      {/* Disable Webhook Modal — matches MVC #disableWebhookModal + #confirmDisableWebhookBtn */}
      {showDisableModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">Disable Webhook</h3>
                <button type="button" className="btn-close" onClick={() => setShowDisableModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <span>Are you sure you want to disable webhook notifications? You will no longer receive callbacks when carrier scores change.</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDisableModal(false)}>Cancel</button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    // Matches MVC #confirmDisableWebhookBtn — immediately save with isActive: false
                    const current = watch();
                    saveMutation.mutate({ ...current, isActive: false }, {
                      onSuccess: () => {
                        setValue("isActive", false);
                        queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "webhooks"] });
                        toast.success("Webhook notifications disabled.");
                      },
                      onError: () => {
                        // Matches MVC: revert toggle to enabled on error
                        toast.error("Failed to disable webhook.");
                        setValue("isActive", true);
                      },
                    });
                    setShowDisableModal(false);
                  }}
                  disabled={saveMutation.isPending}
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rotate Secret Modal — matches MVC #rotateSecretModal */}
      {showRotateModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">Rotate Signing Secret</h3>
                <button type="button" className="btn-close" onClick={() => setShowRotateModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <span>Are you sure you want to rotate the signing secret? The current secret will be invalidated immediately.</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowRotateModal(false)}>Cancel</button>
                <button
                  className="btn btn-warning"
                  onClick={() => {
                    rotateMutation.mutate();
                    setShowRotateModal(false);
                  }}
                  disabled={rotateMutation.isPending}
                >
                  Rotate Secret
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookSettings;
